/**
 * Guarded service-worker registration. Only registers in production on the
 * real published domain (never in dev, iframe previews, or Lovable previews).
 *
 * In refused contexts, actively unregisters any existing /sw.js so stale
 * caches don't keep serving old bundles.
 */

const APP_SW_URL = "/sw.js";

function shouldRefuse(): boolean {
  if (!import.meta.env.PROD) return true;
  try {
    if (window.self !== window.top) return true; // iframe
  } catch {
    return true; // cross-origin iframe access throws → still iframe
  }
  const host = window.location.hostname;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com")) return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;
  if (new URLSearchParams(window.location.search).has("sw") &&
      new URLSearchParams(window.location.search).get("sw") === "off") return true;
  return false;
}

async function unregisterAppSW(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(
      regs
        .filter((r) => {
          const url = r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || "";
          return url.endsWith(APP_SW_URL);
        })
        .map((r) => r.unregister()),
    );
  } catch (err) {
    console.warn("[registerSW] unregister failed:", err);
  }
}

export function registerAppSW(): void {
  if (!("serviceWorker" in navigator)) return;

  if (shouldRefuse()) {
    void unregisterAppSW();
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(APP_SW_URL, { scope: "/" })
      .then((reg) => {
        // When a new SW is waiting, activate it immediately so the next
        // navigation/reload serves fresh assets.
        const promoteWaiting = (sw: ServiceWorker | null) => {
          if (sw && sw.state === "installed") {
            sw.postMessage({ type: "SKIP_WAITING" });
          }
        };
        promoteWaiting(reg.waiting);
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              installing.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch((err) => console.warn("[registerSW] register failed:", err));
  });
}
